import { QueryClientProvider } from "@tanstack/react-query";
import { makeQueryClient } from "@/lib/queryClient";
import { ErrorBoundary } from "@/components/system/ErrorBoundary";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { BrowserRouter } from "react-router";
import { MotionProvider } from "@/components/providers/MotionProvider";
import { LenisProvider } from "@/components/providers/LenisProvider";
import { useBuildHealthCheck } from "@/components/system/BuildHealthCheck";
import { ConfigurationErrorScreen } from "@/components/system/ConfigurationErrorScreen";
import { AnalyticsTracker } from "@/components/analytics/AnalyticsTracker";
import { publicEnvErrors, publicEnvMetadata, supabasePublicKey, supabaseUrl } from "./config/publicEnv";
import { AppRoutes } from "./routes";
import { logError } from "@/lib/errors";
import { ScrollProgress } from "./components/animation/ScrollProgress";
import { ScrollToTop } from "@/components/routing/ScrollToTop";
import RouteLoadingFallback from "@/components/system/RouteLoadingFallback";
import DelayedFallback from "@/components/system/DelayedFallback";
import { ChunkReloadBoundary } from "@/components/system/ChunkReloadBoundary";
import { AuthProvider, useAuthReady } from "@/features/auth";
import { DeferredTooltipProvider } from "@/components/system/DeferredTooltipProvider";
import { signalAppReady } from "@/lib/appLoader";
import { useTalentCardReferralOnAuth } from "@/features/talent-wall/hooks/useTalentCardReferral";

// Single canonical toast host (sonner). Legacy `@/hooks/use-toast` and
// `@/lib/toast` both route through this instance.
const SonnerToaster = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// The QueryClient is created inside the React lifecycle (see App below) so
// no cache is ever shared across requests. See src/lib/queryClient.ts.

/**
 * Fires the `rr:app-ready` event exactly once, as soon as auth has
 * hydrated (or after a 1500ms fallback). The inline splash script in
 * index.html listens and fades #app-loader out — so the splash stays
 * up until the first real render, killing the splash → skeleton flash.
 */
const AppReadySignal = () => {
  const authReady = useAuthReady();
  // Credit any pending Talent Card referral once the visitor is authenticated.
  useTalentCardReferralOnAuth();
  useEffect(() => {
    if (authReady) signalAppReady();
  }, [authReady]);
  useEffect(() => {
    const id = window.setTimeout(() => signalAppReady(), 1500);
    return () => window.clearTimeout(id);
  }, []);
  return null;
};

const App = () => {
  const buildHealthCheck = useBuildHealthCheck();

  useEffect(() => {
    if (buildHealthCheck && !buildHealthCheck.passed) {
      logError("app.buildHealthCheck", new Error("Build health check failed"), { details: buildHealthCheck.details });
    }
  }, [buildHealthCheck]);

  const configurationErrors = useMemo(() => {
    const reasons = [...publicEnvErrors];
    if (!supabaseUrl) reasons.push("Supabase URL is missing.");
    if (!supabasePublicKey) reasons.push("Supabase public key is missing.");
    return reasons;
  }, []);

  const hasConfigurationErrors = configurationErrors.length > 0;

  // One QueryClient per app instance, created inside the React lifecycle.
  // Never a module-level singleton — that cache would be shared across all
  // concurrent requests under SSR.
  const [queryClient] = useState(() => makeQueryClient());

  // Web-only history router. The Capacitor/Android HashRouter fork was
  // removed in Phase A of the TanStack Start migration (2026-08-10).
  const Router = BrowserRouter;

  return (
    <ErrorBoundary>
      <MotionProvider>
        <LenisProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
            <DeferredTooltipProvider>
              <Suspense fallback={null}>
                <SonnerToaster />
              </Suspense>
              {hasConfigurationErrors ? (
                <ConfigurationErrorScreen errors={configurationErrors} runtimeEnvApplied={publicEnvMetadata.runtimeEnvApplied} />
              ) : (
                <Router>
                  <ScrollToTop />
                  <ScrollProgress />
                  <AnalyticsTracker />
                  <Suspense fallback={<DelayedFallback delayMs={150}><RouteLoadingFallback /></DelayedFallback>}>
                    <ChunkReloadBoundary>
                      <AppReadySignal />
                      <AppRoutes />
                    </ChunkReloadBoundary>
                  </Suspense>
                </Router>
              )}
            </DeferredTooltipProvider>
            </AuthProvider>
          </QueryClientProvider>
        </LenisProvider>
      </MotionProvider>
    </ErrorBoundary>
  );
};

export default App;
